import * as THREE from 'three';

// Define custom event types
interface InfiniteGridEventMap {
  subdivisionsChanged: { subdivisions: number };
  divisionsChanged: { divisions: number };
  colorChanged: {
    color: THREE.Color | number;
    colorType: 'color1' | 'color2' | 'fog';
  };
  fogChanged: { property: 'near' | 'far'; value: number };
}

// Define the uniforms interface for type safety
interface GridUniforms {
  uSize1: { value: number };
  uSize2: { value: number };
  uColor1: { value: THREE.Color };
  uColor2: { value: THREE.Color };
  uFogColor: { value: THREE.Color };
  uFogNear: { value: number };
  uFogFar: { value: number };
}

export class InfiniteGrid extends THREE.Object3D {
  subdivisions: number;
  divisions: number;
  gridMaterial: THREE.ShaderMaterial & { uniforms: GridUniforms };
  private eventDispatcher: THREE.EventDispatcher;

  constructor(divisions: number = 1, subdivisions: number = 10) {
    super();

    // Create an internal EventDispatcher for custom events
    this.eventDispatcher = new THREE.EventDispatcher();
    this.divisions = divisions;
    this.subdivisions = subdivisions;

    // Create a custom grid shader material
    const gridSize = 100;

    // Create a plane for the grid
    const gridGeometry = new THREE.PlaneGeometry(gridSize * 2, gridSize * 2, 1, 1);

    // Custom grid shader material
    // Uses transparent: false with custom blending so the grid renders in the
    // opaque pass and participates in depth testing with scene objects.
    this.gridMaterial = new THREE.ShaderMaterial({
      uniforms: {
        uSize1: { value: this.divisions / this.subdivisions },
        uSize2: { value: this.divisions },
        uColor1: { value: new THREE.Color(0x444444) },
        uColor2: { value: new THREE.Color(0x666666) },
        uFogColor: { value: new THREE.Color(0x2a2a2a) },
        uFogNear: { value: 20.0 },
        uFogFar: { value: 60.0 },
      },
      vertexShader: `
   #include <common>
   #include <logdepthbuf_pars_vertex>
   varying vec3 worldPosition;

   void main() {
     worldPosition = position.xzy;
     gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
     #include <logdepthbuf_vertex>
   }
 `,
      fragmentShader: `
   uniform float uSize1;
   uniform float uSize2;
   uniform vec3 uColor1;
   uniform vec3 uColor2;
   uniform vec3 uFogColor;
   uniform float uFogNear;
   uniform float uFogFar;

   varying vec3 worldPosition;

   #include <logdepthbuf_pars_fragment>

   float getGrid(float size) {
     vec2 r = worldPosition.xz / size;
     vec2 grid = abs(fract(r - 0.5) - 0.5) / fwidth(r);
     float line = min(grid.x, grid.y);
     return 1.0 - min(line, 1.0);
   }

   void main() {
     float g1 = getGrid(uSize1);
     float g2 = getGrid(uSize2);

     // Calculate fog factor using both near and far
     float dist = length(worldPosition);
     float fogFactor = smoothstep(uFogNear, uFogFar, dist);

     // Mix grid colors
     vec3 color = mix(uColor1, uColor2, g2);

     // Apply fog to color and use inverse fog factor for alpha (fade out with distance)
     vec3 finalColor = mix(color, uFogColor, fogFactor);
     float alpha = (g1 + g2) * (1.0 - fogFactor);

     // Discard fully transparent fragments so empty space between grid lines
     // does not write to the depth buffer and block objects below the grid
     if (alpha < 0.01) discard;

     gl_FragColor = vec4(finalColor, alpha);
     #include <logdepthbuf_fragment>
   }
 `,
      transparent: false,
      side: THREE.DoubleSide,
      depthWrite: true,
      blending: THREE.CustomBlending,
      blendSrc: THREE.SrcAlphaFactor,
      blendDst: THREE.OneMinusSrcAlphaFactor,
      blendEquation: THREE.AddEquation,
    }) as THREE.ShaderMaterial & { uniforms: GridUniforms };

    // Create the grid mesh
    const gridMesh = new THREE.Mesh(gridGeometry, this.gridMaterial as any);
    gridMesh.renderOrder = -1;
    (gridMesh as any).rotation.x = -Math.PI / 2;
    this.add(gridMesh);
    // return gridMesh;
  }

  setSubdivisions(subdivisions: number): void {
    this.subdivisions = subdivisions;
    this.gridMaterial.uniforms.uSize1.value = this.divisions / this.subdivisions;
    this.eventDispatcher.dispatchEvent({
      type: 'subdivisionsChanged',
      subdivisions,
    });
  }

  setDivisions(divisions: number): void {
    this.divisions = divisions;
    this.gridMaterial.uniforms.uSize1.value = this.divisions / this.subdivisions;
    this.gridMaterial.uniforms.uSize2.value = this.divisions;
    this.eventDispatcher.dispatchEvent({ type: 'divisionsChanged', divisions });
  }

  setColor1(color: THREE.Color | number): void {
    if (typeof color === 'number') {
      this.gridMaterial.uniforms.uColor1.value.setHex(color);
    } else {
      this.gridMaterial.uniforms.uColor1.value.copy(color);
    }
    this.eventDispatcher.dispatchEvent({
      type: 'colorChanged',
      color,
      colorType: 'color1',
    });
  }

  setColor2(color: THREE.Color | number): void {
    if (typeof color === 'number') {
      this.gridMaterial.uniforms.uColor2.value.setHex(color);
    } else {
      this.gridMaterial.uniforms.uColor2.value.copy(color);
    }
    this.eventDispatcher.dispatchEvent({
      type: 'colorChanged',
      color,
      colorType: 'color2',
    });
  }

  setFogColor(color: THREE.Color | number): void {
    if (typeof color === 'number') {
      this.gridMaterial.uniforms.uFogColor.value.setHex(color);
    } else {
      this.gridMaterial.uniforms.uFogColor.value.copy(color);
    }
    this.eventDispatcher.dispatchEvent({
      type: 'colorChanged',
      color,
      colorType: 'fog',
    });
  }

  setFogNear(near: number): void {
    const fogFar = this.gridMaterial.uniforms.uFogFar.value;
    const clampedNear = Math.min(near, fogFar);
    this.gridMaterial.uniforms.uFogNear.value = clampedNear;
    this.eventDispatcher.dispatchEvent({
      type: 'fogChanged',
      property: 'near',
      value: clampedNear,
    });
  }

  setFogFar(far: number): void {
    const fogNear = this.gridMaterial.uniforms.uFogNear.value;
    const clampedFar = Math.max(far, fogNear);
    this.gridMaterial.uniforms.uFogFar.value = clampedFar;
    this.eventDispatcher.dispatchEvent({
      type: 'fogChanged',
      property: 'far',
      value: clampedFar,
    });
  }

  dispose(): void {
    this.gridMaterial.dispose();
    for (const child of this.children) {
      const mesh = child as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
    }
  }

  // Custom event listener methods (prefixed to avoid conflicts with Object3D)
  addGridEventListener<K extends keyof InfiniteGridEventMap>(
    type: K,
    listener: (event: InfiniteGridEventMap[K] & { type: K }) => void
  ): void {
    this.eventDispatcher.addEventListener(type, listener);
  }

  removeGridEventListener<K extends keyof InfiniteGridEventMap>(
    type: K,
    listener: (event: InfiniteGridEventMap[K] & { type: K }) => void
  ): void {
    this.eventDispatcher.removeEventListener(type, listener);
  }

  hasGridEventListener<K extends keyof InfiniteGridEventMap>(
    type: K,
    listener: (event: InfiniteGridEventMap[K] & { type: K }) => void
  ): boolean {
    return this.eventDispatcher.hasEventListener(type, listener);
  }
}
